import { IconButton, Tooltip } from '@mui/material';
import {
  GridColDef,
  getGridStringOperators,
  getGridDateOperators,
  GridFilterInputValueProps,
} from '@mui/x-data-grid-pro';
import moment from 'moment';

import { IcDotSpread } from '@/assets/images';
import { FlagChips } from '@/components';
import { excludeValues } from '@/components/DataGrid/utils';
import { DATE_FORMAT } from '@/config';
import { EthnicOriginList, RaceList } from '@/constants/form_inputs';
import { AddressStatus, IAddress, IMasterNamePerson } from '@/models';
import { PhoneNumbersCell } from '@/pages/MasterIndex/components/PhoneNumbersCell';
import { maskSensitiveString } from '@/pages/MasterIndex/Persons/data';
import { getAddressLocation } from '@/services';
import { formatPersonName, generateUniqueId, getUpdatedLabel } from '@/utils';

const PersonName = ({ person }: { person: IMasterNamePerson }) => (
  <span>{formatPersonName(person)}</span>
);

const uniqueId = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    const { uniqueId: uId } = params.row;
    return generateUniqueId('PER', uId);
  },
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
  valueGetter: (_, row) => generateUniqueId('PER', row.uniqueId),
});

const name = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    return <PersonName person={params.row} />;
  },
  filterOperators: getGridStringOperators().filter(
    excludeValues([
      'isNotEmpty',
      'isEmpty',
      'isAnyOf',
      'equals',
      'doesNotEqual',
    ]),
  ),
  // Must match what PersonName renders: this value drives sorting, CSV export
  // AND the column's filterOperators, so a user filtering on what they see
  // ("Doe John") has to hit. Previously it emitted First Middle Last while the
  // cell showed Last First Middle — the same column disagreeing with itself.
  valueGetter: (_, row) => formatPersonName(row),
});

// Custom filter component that looks exactly like the original but is disabled
const DisabledFilterInput = (props: GridFilterInputValueProps) => {
  // Use the standard "contains" filter input which is a text field
  const StandardFilterInput = getGridStringOperators().find(
    (op) => op.value === 'contains',
  )?.InputComponent;

  // Render the same component but with disabled prop
  if (StandardFilterInput) {
    return (
      <Tooltip title="Please search by name first to enable address filtering">
        <div>
          <StandardFilterInput
            {...props}
            disabled={true}
            placeholder="Filter disabled until name search"
          />
        </div>
      </Tooltip>
    );
  }

  // Fallback (should not happen)
  return (
    <Tooltip title="Please search by name first to enable address filtering">
      <div style={{ padding: '8px', color: '#999' }}>Filter disabled</div>
    </Tooltip>
  );
};

const address = (isNameFilterActive?: boolean): Partial<GridColDef> => {
  // Get the standard string operators
  const operators = getGridStringOperators().filter(
    excludeValues(['isAnyOf', 'equals', 'doesNotEqual']),
  );

  // Create a modified version of the operators for disabled state
  const disabledOperators = operators.map((operator) => ({
    ...operator,
    InputComponent: DisabledFilterInput,
  }));

  return {
    renderCell: (params) => {
      let addressText = '/';

      // For records with an 'addresses' array (manually added)
      if (params.row?.addresses?.length > 0) {
        // Try to find an active address first
        const activeAddress = params.row.addresses.find(
          (item: IAddress) => item.status === AddressStatus.ACTIVE,
        );
        const addressObj = activeAddress || params.row.addresses[0];
        addressText = getAddressLocation(addressObj) || '/';
      }
      // For records with a single 'address' object (imported)
      else if (params.row?.address) {
        addressText = getAddressLocation(params.row.address) || '/';
      }

      if (!isNameFilterActive) {
        return (
          <Tooltip title="Please search by name first to enable address filtering">
            <span>{addressText}</span>
          </Tooltip>
        );
      }

      return addressText;
    },
    // Use the appropriate set of operators based on the name filter state
    filterOperators: isNameFilterActive ? operators : disabledOperators,
    // Improve valueGetter to handle both address formats correctly
    valueGetter: (_, row) => {
      // For manually added records with addresses array
      if (row?.addresses?.length > 0) {
        const activeAddress = row.addresses.find(
          (item: IAddress) => item.status === AddressStatus.ACTIVE,
        );
        const addressObj = activeAddress || row.addresses[0];
        return getAddressLocation(addressObj) || '';
      }
      // For imported records with single address object
      else if (row.address) {
        return getAddressLocation(row.address) || '';
      }
      return '';
    },
  };
};

const birthday = (): Partial<GridColDef> => ({
  renderCell: (params) =>
    params.row.birthday ? moment(params.row.birthday).format(DATE_FORMAT) : '/',
  filterOperators: getGridDateOperators(false).filter(
    excludeValues(['isNotEmpty', 'isEmpty']),
  ),
});

const phone = (): Partial<GridColDef> => ({
  renderCell: ({ row }: { row: IMasterNamePerson }) => (
    <PhoneNumbersCell phoneNumbers={row.phoneNumbers} />
  ),
  filterOperators: getGridStringOperators().filter(
    excludeValues([
      'equals',
      'doesNotEqual',
      'isAnyOf',
      'startsWith',
      'endsWith',
    ]),
  ),
  valueGetter: (_, row) => row.phoneNumbers?.[0]?.phoneNumber,
});

const ethnicity = (): Partial<GridColDef> => ({
  renderCell: (params) =>
    EthnicOriginList[
      params.row.ethnicOrigin as keyof typeof EthnicOriginList
    ] || '/',
  filterable: false,
  valueGetter: (_, row) =>
    EthnicOriginList[row.ethnicOrigin as keyof typeof EthnicOriginList],
});

const race = (): Partial<GridColDef> => ({
  renderCell: (params) =>
    RaceList[params.row.race as keyof typeof RaceList] || '/',
  filterable: false,
});

const licenseId = (): Partial<GridColDef> => ({
  renderCell: (params) => params.row.driverLicense?.number || '/',
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
  valueGetter: (_value, row) => row.driverLicense?.number || '',
});

const driverLicense = (): Partial<GridColDef> => ({
  field: 'driverLicense.number',
  renderCell: (params) => {
    return params.row.driverLicense?.number || '/';
  },
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
  valueGetter: (_, row) => {
    return row.driverLicense?.number || '';
  },
});

const ssn = (): Partial<GridColDef> => ({
  filterOperators: getGridStringOperators().filter(
    excludeValues([
      'equals',
      'doesNotEqual',
      'isAnyOf',
      'startsWith',
      'endsWith',
    ]),
  ),
  valueFormatter: (value: string | undefined | null) =>
    maskSensitiveString(value),
});

const inmateId = (): Partial<GridColDef> => ({
  valueGetter: (_, row) => {
    return row?.inmate?.iid;
  },
  filterOperators: getGridStringOperators().filter(
    excludeValues([
      'isNotEmpty',
      'isEmpty',
      'isAnyOf',
      'contains',
      'doesNotContain',
      'startsWith',
      'endsWith',
    ]),
  ),
});

const lastUpdated = (): Partial<GridColDef> => ({
  renderCell: (params) => getUpdatedLabel(params.row?.updatedAt),
  sortable: false,
  filterable: false,
});

const actions = (
  openMenu?: (
    event: React.MouseEvent<HTMLButtonElement>,
    row: IMasterNamePerson,
  ) => void,
): Partial<GridColDef> => ({
  width: 96,
  sortable: false,
  filterable: false,
  align: 'center',
  headerAlign: 'center',
  disableColumnMenu: true,
  renderCell: (params) => (
    <IconButton
      onClick={(event) => {
        event.stopPropagation();
        openMenu?.(event, params.row);
      }}
    >
      <IcDotSpread />
    </IconButton>
  ),
});

const flags = (): Partial<GridColDef> => ({
  field: 'flags',
  sortable: false,
  filterable: false,
  renderCell: (params) => {
    const f = params.row?.flags || [];

    if (f.length === 0) {
      return '-';
    }

    const flagDetails = f.map((item: any) => ({
      _id: item._id,
      name: item.flag?.name,
      code: item.flag?.code,
      description: item.flag?.description || '',
      origin: item.origin,
      dateAssigned: item.dateAssigned,
      reason: item.reason,
    }));

    return (
      <FlagChips
        flags={flagDetails}
        size="small"
        maxDisplay={2}
        flexWrap="nowrap"
      />
    );
  },
});

export const masterPersonsCols = (
  _navigate?: (path: string) => void,
  _onDelete?: (row: IMasterNamePerson) => void,
  openMenu?: (
    event: React.MouseEvent<HTMLButtonElement>,
    row: IMasterNamePerson,
  ) => void,
  isNameFilterActive?: boolean,
) => {
  return {
    uniqueId,
    name,
    address: () => address(isNameFilterActive),
    flags,
    birthday,
    phone,
    ethnicity,
    race,
    licenseId,
    driverLicense,
    ssn,
    lastUpdated,
    inmateId,
    actions: () => actions(openMenu),
  };
};
